import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOfferteById, downloadOffertePDF } from '../../api/offerte';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, Edit3, Download, ArrowLeft, FileText, CheckCircle, User, Calendar, Phone, Mail } from 'lucide-react';
import { saveAs } from 'file-saver';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { motion } from 'framer-motion';

// Color scheme constants consistent with OffertesManager
const COLORS = {
  primary: "#BFFF00", // Neon green
  secondary: "#1E4FCF", // Blue
  accent: "#FF6B00", // Orange
  dark: "#05081A", // Dark blue/black
}

const OfferteDetails = () => {
  const { offerteId } = useParams();
  const navigate = useNavigate();
  const [offerte, setOfferte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!offerteId) {
      setError("Geen offerte ID opgegeven.");
      setLoading(false);
      return;
    }

    getOfferteById(offerteId)
      .then(responseData => {
        if (responseData && typeof responseData === 'object' && responseData.id) {
          let itemsArray = [];
          if (responseData.items && typeof responseData.items === 'string') {
            try {
              itemsArray = JSON.parse(responseData.items);
            } catch (e) {
              console.error("Fout bij parsen van offerte items:", e);
            }
          } else if (Array.isArray(responseData.items)) {
            itemsArray = responseData.items;
          } else {
            console.warn("Offerte items zijn in een onverwacht formaat:", responseData.items);
          }
          
          setOfferte({ ...responseData, items: itemsArray });
          setError(null);
        } else {
          console.error("Ongeldige of geen data ontvangen van getOfferteById:", responseData);
          setError('Kon offertegegevens niet correct laden of data is incompleet.');
          setOfferte(null);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error in getOfferteById catch:", err);
        setError('Kon offerte details niet laden.');
        setOfferte(null);
        setLoading(false);
      });
  }, [offerteId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };  
  
  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return amount.toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
    } else if (typeof amount === 'string' && !isNaN(parseFloat(amount))) {
      return parseFloat(amount).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
    }
    return '-';
  };

  const handleDownloadPDF = async () => {
    if (!offerte) return;
    setIsDownloading(true);
    setError(null);
    try {
      const blob = await downloadOffertePDF(offerte.id);
      saveAs(blob, `Offerte-${offerte.nummer || offerte.id}.pdf`);
      setSuccessMessage("PDF succesvol gedownload!");
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError("Kon PDF niet downloaden.");
    } finally {
      setIsDownloading(false);
    }
  };
  
  const getStatusBadgeVariant = (status) => {
    switch (String(status).toLowerCase()) {
      case 'concept': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'verzonden': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'geaccepteerd': return 'bg-green-100 text-green-800 border-green-300';
      case 'geweigerd': return 'bg-red-100 text-red-800 border-red-300';
      case 'verlopen': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-3 border-gray-100 border-t-[#BFFF00] border-r-[#BFFF00] animate-spin"></div>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-xs font-medium text-gray-400">LADEN</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (error && !offerte) {
    return (
      <Alert variant="destructive" className="border border-red-200 bg-red-50 text-red-800 rounded-lg m-4">
        <div className="flex gap-2 items-center">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertDescription className="font-medium">{error}</AlertDescription>
        </div>
      </Alert>
    );
  }
  
  if (!offerte) {
    return (
      <div className="text-center p-10 text-gray-500">
        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="font-medium">Offerte niet gevonden.</p>
        <Button 
          variant="outline" 
          onClick={() => navigate('/dashboard/offertes')}
          className="mt-4 border-gray-300"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
        </Button>
      </div>
    );
  }

  // Gebruik het btwPercentage van de offerte data. Fallback naar 0 als het niet gedefinieerd is.
  const effectiveBtwPercentage = offerte.btwPercentage !== undefined && offerte.btwPercentage !== null ? parseFloat(offerte.btwPercentage) : 0;
  
  // Bereken het BTW bedrag op basis van totaalExclBTW en effectiveBtwPercentage
  const berekendBtwBedrag = (parseFloat(offerte.totaalExclBTW || 0) * effectiveBtwPercentage) / 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <div className="flex justify-between items-center">
        <div className="relative">
          <h1 className="text-2xl font-bold text-gray-900">Offerte Details</h1>
          <div className="absolute bottom-0 left-0 w-1/2 h-1 bg-[#BFFF00] -z-10 transform translate-y-1 opacity-60"></div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard/offertes')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50/80 rounded-md"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate(`/dashboard/offertes/edit/${offerte.id}`)}
            className="border-[#1E4FCF] text-[#1E4FCF] hover:bg-[#1E4FCF]/5 rounded-md"
          >
            <Edit3 className="mr-2 h-4 w-4" /> Bewerken
          </Button>
          <Button 
            onClick={handleDownloadPDF} 
            disabled={isDownloading}
            className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
          >
            <Download className="mr-2 h-4 w-4" /> {isDownloading ? 'Downloaden...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="border border-red-200 bg-red-50 text-red-800 rounded-lg">
          <div className="flex gap-2 items-center">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="font-medium">{error}</AlertDescription>
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert className="border border-green-200 bg-green-50 text-green-800 rounded-lg">
          <div className="flex gap-2 items-center">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription className="font-medium">{successMessage}</AlertDescription>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="col-span-1 border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-[#1E4FCF]" /> Klantgegevens
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="min-w-8 flex justify-center">
                <User className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Naam</p>
                <p className="font-medium">{offerte.klantNaam || '-'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="min-w-8 flex justify-center">
                <Mail className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{offerte.klantEmail || '-'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="min-w-8 flex justify-center">
                <Phone className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Telefoon</p>
                <p className="font-medium">{offerte.klantTelefoon || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#1E4FCF]" /> Offerte Informatie
            </CardTitle>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold">#{offerte.nummer || offerte.id}</span>
              <Badge variant="outline" className={`text-xs font-medium px-2 py-0.5 rounded-full border ${getStatusBadgeVariant(offerte.status)}`}>
                {offerte.status ? offerte.status.charAt(0).toUpperCase() + offerte.status.slice(1) : 'Onbekend'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Offertedatum</p>
                <div className="flex items-center mt-1 gap-2">
                  <Calendar className="h-4 w-4 text-[#1E4FCF]" />
                  <p className="font-medium">{formatDate(offerte.offerteDatum)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Vervaldatum</p>
                <div className="flex items-center mt-1 gap-2">
                  <Calendar className="h-4 w-4 text-[#1E4FCF]" />
                  <p className="font-medium">{formatDate(offerte.vervalDatum)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <CardHeader className="pb-2 border-b border-gray-100">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#1E4FCF]" /> Items
          </CardTitle>
          <CardDescription>
            Gespecificeerde diensten en producten in deze offerte
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Naam/Beschrijving</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Aantal</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Prijs/stuk</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Subtotaal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-gray-100">
                {Array.isArray(offerte.items) && offerte.items.length > 0 ? (
                  offerte.items.map((item, index) => (
                    <TableRow key={item.id || index} className="hover:bg-gray-50">
                      <TableCell className="px-4 py-3.5 text-sm text-gray-700">{item.naam || '-'}</TableCell>
                      <TableCell className="px-4 py-3.5 text-sm text-gray-700 text-right">{item.aantal || 0}</TableCell>
                      <TableCell className="px-4 py-3.5 text-sm text-gray-700 text-right">{formatCurrency(item.prijsPerStuk)}</TableCell>
                      <TableCell className="px-4 py-3.5 text-sm text-gray-700 text-right font-medium">{formatCurrency((item.aantal || 0) * (item.prijsPerStuk || 0))}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                      Geen items gevonden in deze offerte.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-end p-4 border-t border-gray-100">
            <div className="w-full md:w-1/3 space-y-2 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex justify-between text-sm">
                  <span>Subtotaal (Excl. BTW):</span> 
                  <span className="font-medium">{formatCurrency(parseFloat(offerte.totaalExclBTW || 0))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>BTW ({effectiveBtwPercentage}%):</span> 
                  <span className="font-medium">{formatCurrency(berekendBtwBedrag)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t border-gray-300 pt-2 mt-2">
                  <span>Totaal (Incl. BTW):</span> 
                  <span className="text-[#1E4FCF]">{formatCurrency(parseFloat(offerte.totaalInclBTW || 0))}</span>
                </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {offerte.extraTekstOfferte && (
        <Card className="border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#1E4FCF]" /> Extra Informatie
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{offerte.extraTekstOfferte}</p>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

export default OfferteDetails;